import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { CaptureEditPanel } from "./CaptureEditPanel";
import { AddTagDialog } from "./AddTagDialog";
import api from "../lib/api";

// Modal wrapper around the shared CaptureEditPanel - used by mobile
// dashboard, and by the favorite-notes shortcuts on Tasks/Schedule pages.
// Desktop Notes dashboard instead renders CaptureEditPanel inline inside
// NotesWorkspace (tabbed split-view), never through this modal.
export function CaptureEditDialog({ capture, open, onOpenChange, onDelete, onSaved }) {
  const { t } = useTranslation();
  const panelRef = useRef(null);
  const isText = capture?.type === "text";

  // Tagging is self-contained here (fetches its own tag list + owns its
  // own AddTagDialog) instead of requiring every parent that renders this
  // modal (Dashboard grid, MobileDashboard, Tasks/Schedule favorite-notes
  // shortcuts, TaskDetailDialog's nested linked-note editor) to thread a
  // tags list + tag-dialog-open state through individually - this makes
  // "add tag" work the same way in every note detail editor with zero
  // per-parent wiring.
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [tags, setTags] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/tags").then(({ data }) => setTags(data)).catch(() => {});
  }, [open]);

  const handleOpenChange = async (isOpen) => {
    if (!isOpen) await panelRef.current?.finalizeAndSave();
    onOpenChange(isOpen);
  };

  if (!capture) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className={`${isText ? "max-w-2xl" : "max-w-4xl"} rounded-none max-h-[90vh] overflow-y-auto`} data-testid="capture-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{isText ? t("editDialog.titleText") : t("editDialog.titleImage")}</DialogTitle>
          <DialogDescription className="sr-only">{isText ? t("editDialog.titleText") : t("editDialog.titleImage")}</DialogDescription>
        </DialogHeader>
        <CaptureEditPanel
          ref={panelRef}
          capture={capture}
          onDelete={onDelete}
          onSaved={onSaved}
          onAddTag={() => setTagDialogOpen(true)}
          tags={tags}
        />
      </DialogContent>

      <AddTagDialog
        capture={capture}
        tags={tags}
        open={tagDialogOpen}
        onOpenChange={setTagDialogOpen}
        onTagged={onSaved}
        onCreateTag={(newTag) => setTags((prev) => [newTag, ...prev])}
      />
    </Dialog>
  );
}
